/*
  Manse — Hanok Pantry
  Small, dependency-free behaviours for Manse sections:
  - quick add buttons on Manse product cards
  - tabbed product grid
  - build-a-box
  Cart changes go through Shopify.actions.updateCart so Horizon's cart drawer,
  cart count and cart page all refresh the normal way.
*/

import { formatMoney } from '@theme/money-formatting';

/**
 * Adds lines to the cart and opens the cart drawer.
 * Falls back to the AJAX cart API and the /cart page if standard actions are missing.
 * @param {{ id: string|number, quantity: number, properties?: Record<string,string> }[]} items
 */
async function addToCart(items) {
  const actions = window.Shopify?.actions;

  if (actions?.updateCart) {
    const result = await actions.updateCart({
      lines: items.map((item) => ({
        merchandiseId: String(item.id),
        quantity: item.quantity,
        attributes: Object.entries(item.properties || {}).map(([key, value]) => ({ key, value })),
      })),
    });

    if (result?.userErrors?.length) {
      throw new Error(result.userErrors[0].message);
    }

    if (actions.openCart) {
      await actions.openCart();
    }
    return;
  }

  const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
  const response = await fetch(cartAddUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: items.map((item) => ({ id: Number(item.id), quantity: item.quantity, properties: item.properties || {} })),
    }),
  });
  const data = await response.json();
  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || 'Could not add to cart');
  }
  window.location.href = window.Theme?.routes?.cart_url || '/cart';
}

/* ---------- Quick add ---------- */

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('[data-mh-add]');
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();
  const label = button.dataset.mhLabel || button.textContent?.trim() || '';
  button.classList.add('is-loading');
  button.setAttribute('aria-busy', 'true');

  try {
    await addToCart([{ id: button.dataset.mhAdd || '', quantity: 1 }]);
    button.classList.add('is-added');
    button.textContent = 'Added';
  } catch (error) {
    button.textContent = error instanceof Error && error.message ? error.message : 'Try again';
  } finally {
    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
    window.setTimeout(() => {
      button.classList.remove('is-added');
      button.textContent = label;
    }, 2200);
  }
});

/* ---------- Tabbed product grid ---------- */

class ManseTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => this.onKey(event, index));
    });
  }

  /** @param {number} index */
  select(index, focus = false) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active && focus) tab.focus();
    });
    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
  }

  /** @param {KeyboardEvent} event @param {number} index */
  onKey(event, index) {
    const last = this.tabs.length - 1;
    let next = null;
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    this.select(next, true);
  }
}

if (!customElements.get('manse-tabs')) customElements.define('manse-tabs', ManseTabs);

/* ---------- Build a box ---------- */

class ManseBoxBuilder extends HTMLElement {
  connectedCallback() {
    this.size = Number(this.dataset.size) || 5;
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.currency = this.dataset.currency || 'PKR';
    /** @type {{ id: string, price: number, image: string, title: string }[]} */
    this.box = [];

    this.slots = Array.from(this.querySelectorAll('[data-slot]'));
    this.countEl = this.querySelector('[data-count]');
    this.totalEl = this.querySelector('[data-total]');
    this.barEl = this.querySelector('[data-bar]');
    this.submit = this.querySelector('[data-submit]');
    this.errorEl = this.querySelector('[data-error]');
    this.cardInputs = Array.from(this.querySelectorAll('[data-card-input]'));

    this.querySelectorAll('[data-pick]').forEach((pick) => {
      pick.addEventListener('click', () => this.add(/** @type {HTMLElement} */ (pick)));
    });
    this.slots.forEach((slot, index) => {
      slot.addEventListener('click', () => this.remove(index));
    });
    this.cardInputs.forEach((input) => input.addEventListener('change', () => this.render()));
    this.submit?.addEventListener('click', () => this.checkout());

    this.render();
  }

  /** @param {HTMLElement} pick */
  add(pick) {
    if (this.box.length >= this.size) {
      this.showError(`Your box is full. Tap a slot to swap a flavour.`);
      return;
    }
    this.box.push({
      id: pick.dataset.variantId || '',
      price: Number(pick.dataset.price) || 0,
      image: pick.dataset.image || '',
      title: pick.dataset.title || '',
    });
    this.showError('');
    this.render();
  }

  /** @param {number} index */
  remove(index) {
    if (!this.box[index]) return;
    this.box.splice(index, 1);
    this.showError('');
    this.render();
  }

  /** @param {string} message */
  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }

  selectedCard() {
    const checked = this.cardInputs.find((input) => input instanceof HTMLInputElement && input.checked);
    return checked instanceof HTMLInputElement ? checked.value : '';
  }

  render() {
    this.slots.forEach((slot, index) => {
      const item = this.box[index];
      slot.classList.toggle('is-full', Boolean(item));
      slot.innerHTML = '';
      if (item) {
        if (item.image) {
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = '';
          img.loading = 'lazy';
          slot.append(img);
        }
        slot.setAttribute('aria-label', `Remove ${item.title} from slot ${index + 1}`);
      } else {
        slot.textContent = String(index + 1);
        slot.setAttribute('aria-label', `Empty slot ${index + 1}`);
      }
    });

    const total = this.box.reduce((sum, item) => sum + item.price, 0);
    if (this.countEl) this.countEl.textContent = `${this.box.length} of ${this.size} chosen`;
    if (this.totalEl) this.totalEl.textContent = formatMoney(total, this.moneyFormat, this.currency);
    if (this.barEl instanceof HTMLElement) this.barEl.style.width = `${(this.box.length / this.size) * 100}%`;

    const needsCard = this.cardInputs.length > 0 && this.dataset.cardRequired === 'true';
    const ready = this.box.length === this.size && (!needsCard || this.selectedCard() !== '');
    if (this.submit instanceof HTMLButtonElement) this.submit.disabled = !ready;
  }

  async checkout() {
    if (!(this.submit instanceof HTMLButtonElement) || this.submit.disabled) return;

    /** @type {Map<string, number>} */
    const counts = new Map();
    this.box.forEach((item) => counts.set(item.id, (counts.get(item.id) || 0) + 1));

    const boxLabel = this.dataset.boxLabel || `Box of ${this.size}`;
    const card = this.selectedCard();
    const items = Array.from(counts.entries()).map(([id, quantity], index) => {
      /** @type {Record<string,string>} */
      const properties = { Box: boxLabel };
      if (index === 0 && card) properties.Photocard = card;
      return { id, quantity, properties };
    });

    const label = this.submit.textContent;
    this.submit.disabled = true;
    this.submit.textContent = 'Adding…';
    try {
      await addToCart(items);
      this.box = [];
      this.showError('');
    } catch (error) {
      this.showError(error instanceof Error && error.message ? error.message : 'Could not add your box. Please try again.');
    } finally {
      this.submit.textContent = label;
      this.render();
    }
  }
}

if (!customElements.get('manse-box-builder')) customElements.define('manse-box-builder', ManseBoxBuilder);
